import { useEffect, useState } from 'react';
import { ListSkeleton } from '../../components/ListSkeleton';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { Plus, Search, ArrowRight, Lock, Upload, Users, Link2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@repo/ui/src/components/ui/button';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@repo/ui/src/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@repo/ui/src/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@repo/ui/src/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@repo/ui/src/components/ui/dropdown-menu';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@repo/ui/src/components/ui/tooltip';
import { cn } from '@repo/ui/src/lib/utils';
import { useStudents, useStudentTags, useCreateStudent } from '../../hooks/useStudents';
import { useSubjects } from '../../hooks/useSubjects';
import { useClientOptions, useClientLookup } from '../../hooks/useClients';
import { useSubscription } from '../../hooks/useSubscription';
import { useProgressLinkSummary, useBulkCreateProgressLinks } from '../../hooks/useProgress';
import { useUpgradeDialogStore } from '../../stores/upgradeDialogStore';
import { useCan } from '../../hooks/useCan';
import { AdminOnlyTooltip } from '../../components/AdminOnlyTooltip';
import { PageHeader, pageHeaderActionClass } from '../../components/PageHeader';
import { PaginationFooter, pageCount } from '../../components/PaginationFooter';
import { Tags } from '../../components/Tags';
import { ClientCombobox } from '../../components/ClientCombobox';
import { ImportWizard } from './import/ImportWizard';
import { StudentFilterPanel, type StudentFilters } from './components/StudentFilterPanel';
import { StudentIdentityFields, type StudentIdentityValues } from './components/StudentIdentityFields';
import { EmptyState } from '../../components/EmptyState';
import { CardList, CardRow } from '../../components/CardList';
import {
  ENROLLMENT_MODES, type ClassColor, type EnrollmentMode, type StudentClassChip, type StudentWithClient,
} from '@repo/shared-types';
import { useEffectiveBranchId } from '../../hooks/useBranchScope';
import { useClasses } from '../../hooks/useClasses';
import { useClassMode } from '../../hooks/useOrgSettings';
import { classColorStyle } from '../../lib/classColors';

const DEBOUNCE = 300;
const SORTS = ['newest', 'oldest', 'name', 'payer'] as const;
type Sort = (typeof SORTS)[number];

const csv = (v: string | null): string[] =>
  v ? v.split(',').map((x) => x.trim()).filter(Boolean) : [];

/** Drops anything that isn't one of the three modes — a shared or bookmarked
 *  URL can carry a value the app no longer knows, and it must not reach the
 *  filter state (or the chips would render a selection nobody can clear). */
const isEnrollmentMode = (v: string): v is EnrollmentMode =>
  (ENROLLMENT_MODES as readonly string[]).includes(v);

/**
 * The khoá badge (13 §7): loud only when it is actionable. `khoaRemaining` is
 * the smallest remaining across the student's live term enrolments, and it can
 * be NEGATIVE on an over-run — both `<= 0` cases read "Hết khoá", because from
 * the teacher's side the action is identical (collect for the next block). A
 * student with 3+ sessions left, or none on a khoá at all, gets nothing: the
 * badge is a nudge, not a status column.
 */
// i18next's TFunction isn't structurally assignable to a plain callback (see
// lib/bookkeepingLabels.ts), so call sites pass `t as KhoaBadgeT`.
type KhoaBadgeT = (k: string, o?: object) => string;
// A plain function, not a component: CardRow's `right` slot renders
// `right ? <span>...</span> : null`, and a `<KhoaBadge />` ELEMENT is always
// truthy regardless of what it renders to, so the common "no badge" case was
// still opening the wrapper span/gap on the card. Returning the node (or
// null) directly makes that check see the real answer.
function khoaBadge(remaining: number | null | undefined, t: KhoaBadgeT): React.ReactNode | null {
  if (remaining == null) return null;
  if (remaining <= 0) return <Badge variant="danger" className="font-medium">{t('list.khoaOut')}</Badge>;
  if (remaining <= 2) return <Badge variant="warning" className="font-medium">{t('list.khoaLeft', { count: remaining })}</Badge>;
  return null;
}

/** 8px colour square (spec §5 "Students list", handoff decision 13): filled
 *  tint+ink when the class has a colour, a dashed hollow square when it
 *  doesn't — same convention as `EnrollmentCard`'s `ScheduleColorSquare` and
 *  `ClassListTab`'s `ColorSquare`, just a third copy sized for this table
 *  cell (one implementation each lives in `classColorStyle`; only the
 *  wrapper square is repeated per surface). */
function ClassChipSquare({ color }: { color: ClassColor | null }) {
  const style = classColorStyle(color);
  // Fix wave (review M22) — the comment above already says 8px; the code
  // was `h-2 w-2` (6.4px on this app's 0.2rem `--spacing` scale, §6.1),
  // one of three sizes for the same token across surfaces (the filter
  // popover's square is `h-2.5 w-2.5` = 8px, `ClassListTab`'s is `h-3 w-3`
  // = 9.6px). Matches the filter popover, the one this component's own
  // comment names as the sibling to be consistent with.
  if (!style) {
    return <span className="h-2.5 w-2.5 shrink-0 rounded-[2px] border border-dashed border-surface-300" aria-hidden />;
  }
  return <span className="h-2.5 w-2.5 shrink-0 rounded-[2px] border opacity-90" style={style} aria-hidden />;
}

// i18next's TFunction isn't structurally assignable to a plain callback (see
// KhoaBadgeT above) — same idea, but `options` has to be `Record<string,
// unknown>` (not `object`) so this type is ALSO assignable as a JSX prop
// (`<ClassChips t={t} />`) with no cast, matching ClassListTab's own `ListT`.
type ClassesT = (k: string, o?: Record<string, unknown>) => string;

/** One chip per class (spec §5 "Students list"): colour square + `displayName`,
 *  "(2/3)" appended when the enrollment sits in only some of the class's
 *  khung giờ, linking to `/classes/:id` — `stopPropagation` because the whole
 *  row is itself a link to the student. */
function ClassChips({ classes, t }: { classes: StudentClassChip[]; t: ClassesT }) {
  return (
    <>
      {classes.map((c) => (
        <Link
          key={c.id}
          to={`/classes/${c.id}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-surface-200 bg-surface-50 px-2 py-0.5 text-xs text-surface-700 hover:bg-surface-100 hover:underline"
        >
          <ClassChipSquare color={c.color} />
          {c.displayName}
          {c.joinedSlots < c.totalSlots && (
            <span className="text-surface-500">{t('list.classPartial', { joined: c.joinedSlots, total: c.totalSlots })}</span>
          )}
        </Link>
      ))}
    </>
  );
}

/** Phone card's class line (spec §5 "Students list" — "a class line on each
 *  card"): plain text, not chips — `EnrollmentCard`'s desktop-only "· scheduleText"
 *  reasoning doesn't apply to a list row this dense, and the card's title badge
 *  already carries the "Chưa xếp lớp" signal (see `cardBadge`), so this line
 *  never needs to. */
function classesCardLine(classes: StudentClassChip[], t: ClassesT): string | null {
  if (classes.length === 0) return null;
  return classes
    .map((c) => (c.joinedSlots < c.totalSlots
      ? `${c.displayName} ${t('list.classPartial', { joined: c.joinedSlots, total: c.totalSlots })}`
      : c.displayName))
    .join(' · ');
}

export function StudentsPage() {
  const { t } = useTranslation('students');
  const { t: tProgress } = useTranslation('progress');
  const [params, setParams] = useSearchParams();
  const search = params.get('search') ?? '';
  const showInactive = params.get('status') === 'all';
  const noEnrollment = params.get('noEnrollment') === '1';
  const tags = csv(params.get('tag'));
  // The panel makes noEnrollment + noSlot/subject unreachable, but a URL shared
  // or bookmarked before that guard existed can still carry it — and the query
  // it produces is empty BY CONSTRUCTION (an `enrollments.none` and an
  // `enrollments.some` cannot both hold). Normalise here, where the values are
  // already being derived, so the link behaves like the panel does.
  const noSlot = !noEnrollment && params.get('noSlot') === '1';
  const subjectIds = noEnrollment ? [] : csv(params.get('subject'));
  const modes = noEnrollment ? [] : csv(params.get('mode')).filter(isEnrollmentMode);
  // THI-267 Task 15: classIds folds into the same `enrollments.some` clause as
  // subjectIds/modes/noSlot server-side (useStudents.ts), so it is zeroed the
  // same way under noEnrollment for the same reason (see the noSlot comment
  // above this block).
  const classIds = noEnrollment ? [] : csv(params.get('classIds'));
  const filters: StudentFilters = { showInactive, noEnrollment, noSlot, subjectIds, modes, tags, classIds };
  const sortParam = params.get('sort');
  const sort: Sort = SORTS.includes(sortParam as Sort) ? (sortParam as Sort) : 'newest';
  const page = Number(params.get('page')) || 1;
  const [searchInput, setSearchInput] = useState(search);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkLinkOpen, setBulkLinkOpen] = useState(false);
  // `allowsProgressLink` has three readings, not two, exactly like
  // ProgressLinkBlock (and the Task 8 fix in ReviewTargetsCard, commit
  // cab97629) document: while `subscription` is still pending (a cold load)
  // or the fetch itself failed, reading `allowsProgressLink === true` as
  // `false` would flash the LOCKED affordance at every org — Pro/Center
  // included — for the split second before the plan is known, and a fast
  // click during that window would wrongly pop the upgrade dialog for a
  // feature the org actually owns. Both "pending" and "error" collapse into
  // `progressLinkUnknown` below: the dropdown item renders disabled with its
  // normal icon until the plan is actually known one way or the other.
  const {
    data: subscription, isPending: subscriptionPending, isError: subscriptionError,
  } = useSubscription();
  const allowsAiImport = subscription?.plan.allowsAiImport ?? false;
  const progressLinkUnknown = subscriptionPending || subscriptionError;
  const allowsProgressLink = subscription?.plan.allowsProgressLink === true;
  const linkSummary = useProgressLinkSummary();
  const bulkCreate = useBulkCreateProgressLinks();
  const canManageStudents = useCan('manage:students');

  // Any change to search/tag/status/sort resets to page 1 — page=1 itself is
  // kept out of the URL so links stay clean. Uses the functional form of
  // setParams so it always applies against the CURRENT params at commit
  // time, not whatever `params` looked like when the caller was created —
  // this matters for the debounced search effect below, whose timer can
  // fire after a status/sort click has already updated the URL.
  const setFilter = (key: string, value: string | null) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete('page');
      return next;
    }, { replace: true });
  };

  const setPage = (next: number) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next <= 1) p.delete('page');
      else p.set('page', String(next));
      return p;
    }, { replace: true });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      // No-op debounce (e.g. a shared/refreshed URL landing with `search`
      // already set to this value) must not call setFilter — that would
      // strip `page` from the URL even though nothing actually changed.
      if (searchInput === search) return;
      setFilter('search', searchInput || null);
    }, DEBOUNCE);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const branchId = useEffectiveBranchId();
  const { data, isLoading, isError } = useStudents({
    search: search || undefined,
    status: showInactive ? 'all' : undefined,
    noEnrollment,
    noSlot,
    subjectIds,
    modes,
    tags,
    classIds,
    sort: sort === 'newest' ? undefined : sort,
    page: page > 1 ? page : undefined,
    branchId,
  });
  const { data: allTags } = useStudentTags();
  const { data: allSubjects } = useSubjects();
  // THI-267 Task 15: the Lớp filter popover needs ended classes listed after
  // active ones (spec §5 "Students list", handoff decision 13) — ARCHIVED is
  // never fetched, so it can never appear as a filter option. Two `useClasses`
  // calls concatenated in this order rather than one, because `GET /classes`
  // only ever returns one `phase` bucket per `status` (see `useClasses.ts`'s
  // own `ClassFilters['status']` doc). Fetched unconditionally (not gated on
  // the popover being open) so the applied-filter chip row below can resolve
  // a `classIds` id to a name even before the popover has ever been opened.
  const { data: activeClasses } = useClasses({ status: 'ACTIVE' });
  const { data: endedClasses } = useClasses({ status: 'ENDED' });
  const allClasses = [...(activeClasses ?? []), ...(endedClasses ?? [])];
  const classMode = useClassMode();
  // Column follows classMode (spec §2 decision 12: no org setting for it) but
  // stays reachable through its own filter — applying a Lớp filter in
  // INDIVIDUAL mode reveals the column for that view so the user can see what
  // they filtered by. The same flag also gates the phone card's class line.
  const showClassColumn = classMode === 'COHORT' || classIds.length > 0;

  // A page beyond the last one (stale link, or the last item on the last
  // page got deleted) would otherwise render the "no students at all" empty
  // state instead of the real last page.
  useEffect(() => {
    if (data && data.items.length === 0 && data.total > 0 && page > 1) {
      setPage(pageCount(data.total, data.pageSize));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const setSort = (next: Sort) => setFilter('sort', next === 'newest' ? null : next);

  // Functional form for the same reason as setFilter above: a filter toggle
  // can land inside the search debounce window, and must not be clobbered by
  // (or itself clobber) the pending search commit.
  const setFilters = (next: StudentFilters) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next.showInactive) p.set('status', 'all'); else p.delete('status');
      if (next.noEnrollment) p.set('noEnrollment', '1'); else p.delete('noEnrollment');
      if (next.noSlot) p.set('noSlot', '1'); else p.delete('noSlot');
      if (next.subjectIds.length) p.set('subject', next.subjectIds.join(',')); else p.delete('subject');
      if (next.modes.length) p.set('mode', next.modes.join(',')); else p.delete('mode');
      if (next.tags.length) p.set('tag', next.tags.join(',')); else p.delete('tag');
      if (next.classIds.length) p.set('classIds', next.classIds.join(',')); else p.delete('classIds');
      p.delete('page');
      return p;
    }, { replace: true });
  };

  // `showInactive` WIDENS the ACTIVE-only default rather than narrowing it, so
  // it must not count as "the user filtered and got nothing" — it's excluded
  // here even though `activeFilterCount` (badge) still counts it, by design.
  // Search is handled separately below (searchMiss takes priority over a
  // plain filterMiss when it's what emptied the list).
  const hasOtherNarrowingFilter =
    noEnrollment || noSlot || subjectIds.length > 0 || modes.length > 0 || tags.length > 0 || classIds.length > 0;

  // Same reset shape as the filter panel's own "Xoá" button (StudentFilterPanel) —
  // reused here as the empty-state's "Xoá bộ lọc" action rather than duplicated.
  function clearAllFilters() {
    setFilters({
      showInactive: false, noEnrollment: false, noSlot: false, subjectIds: [], modes: [], tags: [], classIds: [],
    });
  }

  // The empty story is decided ONCE and rendered once — the table and the card
  // list share it, so neither breakpoint can tell a different story (and no
  // colSpan has to track the column count). The four cases and their order are
  // unchanged from the version that lived inside the table cell.
  const emptyNode = search ? (
    <EmptyState kind="searchMiss" query={search} />
  ) : hasOtherNarrowingFilter ? (
    <EmptyState
      kind="filterMiss"
      title={t('list.noResults')}
      body={t('list.filterMissBody')}
      onClear={clearAllFilters}
    />
  ) : showInactive ? (
    // showInactive is on, so this genuinely means zero students — there's no
    // ACTIVE-only default left to hide anything behind.
    <EmptyState
      kind="teach"
      icon={Users}
      title={t('list.empty')}
      body={t('list.emptyTeachBody')}
      action={{ label: t('list.add'), onClick: () => setOpen(true) }}
    />
  ) : (
    // No user filter narrowed anything, but the ACTIVE-only default is still in
    // effect: we cannot tell "no students at all" apart from "every student is
    // stopped" without another query (and we deliberately don't add one) — so
    // the copy stays true either way.
    <EmptyState
      kind="filterMiss"
      title={t('list.emptyActiveOnly')}
      body={t('list.filterMissBody')}
      onClear={() => setFilters({ ...filters, showInactive: true })}
    />
  );

  /** One badge slot on the card: a stopped student, or the one setup gap that
   *  matters. Never both — PUT /students/:id → INACTIVE ends every ACTIVE
   *  enrolment, so the warning chips are already suppressed for INACTIVE rows
   *  in the table for exactly that reason. */
  const cardBadge = (s: StudentWithClient) => {
    if (s.status === 'INACTIVE') {
      return (
        <span className="whitespace-nowrap rounded-md bg-surface-200 px-1.5 py-0.5 text-[11px] font-medium text-surface-600">
          {t('status.INACTIVE')}
        </span>
      );
    }
    if (s.hasEnrollment === false) {
      return (
        <span className="whitespace-nowrap rounded-md bg-attention-tint px-1.5 py-0.5 text-[11px] font-medium text-attention-foreground">
          ⚠ {t('list.noEnrollmentChip')}
        </span>
      );
    }
    if (s.needsSlot) {
      return (
        <span className="whitespace-nowrap rounded-md bg-attention-tint px-1.5 py-0.5 text-[11px] font-medium text-attention-foreground">
          ⚠ {t('list.noSlotChip')}
        </span>
      );
    }
    return null;
  };

  // Shared between the two branches below: when `canManageStudents` is
  // false, AdminOnlyTooltip already wraps this in its own "admins only"
  // tooltip (and the button is disabled, so a second Tooltip on top of it
  // wouldn't fire anyway — disabled elements don't take pointer events).
  // When it's true, AdminOnlyTooltip is a passthrough, so an admin on a
  // phone would otherwise have no way to discover an icon-only button —
  // hence the plain `Tooltip` wrap in the true branch below.
  const bulkLinkButton = (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={!canManageStudents || progressLinkUnknown}
      aria-label={tProgress('link.bulkButton')}
      onClick={() =>
        allowsProgressLink
          ? setBulkLinkOpen(true)
          : useUpgradeDialogStore.getState().open('progressLink')
      }
      className={cn(pageHeaderActionClass, 'gap-2 max-sm:w-[44px] max-sm:flex-none max-sm:px-0')}
    >
      {!progressLinkUnknown && !allowsProgressLink ? (
        <Lock className="h-4 w-4" />
      ) : (
        <Link2 className="h-4 w-4" />
      )}
      {/* Icon-only on phone; the label is the aria-label and the
          tooltip, so the affordance is never nameless. */}
      <span className="max-sm:sr-only">{tProgress('link.bulkButton')}</span>
    </Button>
  );

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto w-full">
      {/* The count lives here rather than only in the pagination footer,
          which disappears entirely at one page — so the teacher could not
          see how many students they had unless they had enough to paginate.
          It counts what the list is currently SHOWING, which is the same
          number the footer reports, so the two can never disagree.

          The Add button is a DropdownMenuTrigger (a private 2-item menu —
          manual vs AI import), not a single click/link target, so it can't
          be expressed as a PageHeaderAction; it goes through `extra` instead
          so the header still owns the action row on mobile. The bulk parent-
          link button (13 §11.2 item 7) sits beside it in the same row: an
          outline button, same secondary pattern as TimetablePage's template
          picker, gated exactly like "Thêm học viên" (RBAC) plus the cycle-2
          plan/pending gate the dropdown item used to carry. */}
      <PageHeader
        size="lg"
        title={t('list.title')}
        subtitle={data ? t('list.countSubtitle', { count: data.total }) : t('list.subtitle')}
        extra={
          <div className="flex flex-1 items-center gap-2 sm:flex-none">
            <AdminOnlyTooltip show={!canManageStudents}>
              {canManageStudents ? (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>{bulkLinkButton}</TooltipTrigger>
                    <TooltipContent>{tProgress('link.bulkButton')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                bulkLinkButton
              )}
            </AdminOnlyTooltip>

            <AdminOnlyTooltip show={!canManageStudents} className="flex-1 sm:flex-none">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" className={`${pageHeaderActionClass} gap-2`} disabled={!canManageStudents}>
                    <Plus className="h-4 w-4" /> {t('list.add')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setOpen(true)}>
                    <Plus className="h-4 w-4" /> {t('list.addManual')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      allowsAiImport ? setImportOpen(true) : useUpgradeDialogStore.getState().open('aiImport')
                    }
                  >
                    {allowsAiImport ? <Upload className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                    {t('list.import')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </AdminOnlyTooltip>
          </div>
        }
      />

      <AlertDialog open={bulkLinkOpen} onOpenChange={setBulkLinkOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tProgress('link.bulkConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {linkSummary.data
                ? linkSummary.data.withoutLink === 0
                  ? tProgress('link.bulkAllLinked')
                  : tProgress('link.bulkConfirmBody', {
                      missing: linkSummary.data.withoutLink, existing: linkSummary.data.withLink,
                    })
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tProgress('link.bulkConfirmCancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={bulkCreate.isPending || !linkSummary.data || linkSummary.data.withoutLink === 0}
              onClick={() =>
                bulkCreate.mutate(undefined, {
                  onSuccess: (d) => {
                    toast.success(tProgress('link.bulkCreatedToast', { count: d.created }));
                    setBulkLinkOpen(false);
                  },
                })
              }
            >
              {tProgress('link.bulkConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder={t('list.search')} className="pl-9" />
        </div>
        <StudentFilterPanel
          filters={filters}
          onChange={setFilters}
          subjects={allSubjects ?? []}
          tags={allTags ?? []}
          classes={allClasses}
        />
        <Select value={sort} onValueChange={(v) => setSort(v as Sort)}>
          <SelectTrigger className="w-[112px] sm:w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">{t('list.sortNewest')}</SelectItem>
            <SelectItem value="oldest">{t('list.sortOldest')}</SelectItem>
            <SelectItem value="name">{t('list.sortName')}</SelectItem>
            <SelectItem value="payer">{t('list.sortPayer')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* THI-267 Task 15: the applied Lớp filter, one removable chip per
          selected class (spec §5 "Students list", handoff decision 13 — "Lớp:
          Toán 8 ×"), same pill shape ClassesPage's showEnded/showArchived
          chips already use. `§6.17` doesn't apply here: this page has no bulk
          selection to clear. */}
      {classIds.length > 0 && (
        <div className="-mt-3 mb-6 flex flex-wrap items-center gap-2">
          {classIds.map((id) => {
            const cls = allClasses.find((c) => c.id === id);
            if (!cls) return null;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setFilters({ ...filters, classIds: classIds.filter((x) => x !== id) })}
                className="inline-flex h-[25.6px] items-center gap-1.5 rounded-full bg-surface-100 pl-[9.6px] pr-1.5 text-[12.5px] font-medium text-surface-600"
              >
                <ClassChipSquare color={cls.color} />
                {t('list.appliedClassChip', { name: cls.displayName })}
                <X className="h-[12.8px] w-[12.8px] text-surface-400" />
              </button>
            );
          })}
        </div>
      )}

      <div className="rounded-md border bg-card">
        {isLoading ? (
          <ListSkeleton label={t('list.loading')} />
        ) : isError ? (
          // Without this a failed request falls into the empty states below and
          // tells the teacher their roster is empty — now with a button that
          // cannot help. Say what actually happened instead.
          <p className="flex h-24 items-center justify-center text-sm text-muted-foreground">{t('list.loadError')}</p>
        ) : !data || data.items.length === 0 ? (
          emptyNode
        ) : (
          <>
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('list.name')}</TableHead>
                    <TableHead>{t('list.nickname')}</TableHead>
                    <TableHead>{t('list.subjects')}</TableHead>
                    {/* THI-267 Task 15: follows classMode (spec §2 decision 12 —
                        no org setting for this column), but stays reachable
                        through its own filter regardless of mode. */}
                    {showClassColumn && <TableHead>{t('list.classesColumn')}</TableHead>}
                    <TableHead>{t('list.payer')}</TableHead>
                    {/* Reference-only, and the least useful of the five on a
                        narrow screen where every column is fighting for width.
                        It survives on the student's own page instead. */}
                    <TableHead className="hidden lg:table-cell">{t('list.added')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((s) => (
                    <TableRow key={s.id} className="cursor-pointer">
                      <TableCell>
                        <Link to={`/students/${s.id}`} className="block">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="font-medium text-surface-900">{s.name}</span>
                            {/* A badge, not a strikethrough. Striking the name was too
                                quiet to notice, and striking the whole row would have
                                made the payer name and phone — still wanted for a
                                stopped student — hard to read.

                                Neutral grey, deliberately not red: red says "something
                                is wrong", and a stopped student is a state, not a
                                fault. It is the same reasoning that suppresses the
                                ochre warning chips for these rows — an archived
                                student needs nothing done to them. */}
                            {s.status === 'INACTIVE' && (
                              <span className="shrink-0 whitespace-nowrap rounded-md bg-surface-200 px-1.5 py-0.5 text-[11px] font-medium text-surface-600">
                                {t('status.INACTIVE')}
                              </span>
                            )}
                          </div>
                        </Link>
                      </TableCell>
                      <TableCell className="text-surface-600">{s.nickname || '—'}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {(s.subjects ?? []).map((sub) => (
                            <span key={sub.id} className="whitespace-nowrap rounded-md border border-surface-200 bg-surface-50 px-2 py-0.5 text-xs text-surface-700">
                              {sub.name}
                            </span>
                          ))}
                          {/* Warning chips use the design system's ochre, which is reserved for
                              "needs attention". They mark exactly what the two filters find, so a
                              small roster needs no filter at all — the gaps show themselves.
                              PUT /students/:id -> INACTIVE ends every ACTIVE enrolment (see
                              studentsApp PUT handler), so an INACTIVE student ALWAYS has
                              hasEnrollment: false — that's the archive working as intended, not
                              a setup gap. Suppress both chips for stopped students; don't
                              "restore" them without first addressing that root cause. */}
                          {s.status !== 'INACTIVE' && s.hasEnrollment === false && (
                            <span className="whitespace-nowrap rounded-md bg-attention-tint px-2 py-0.5 text-xs font-medium text-attention-foreground">
                              ⚠ {t('list.noEnrollmentChip')}
                            </span>
                          )}
                          {/* Moved into the Lớp column below when that column is
                              showing — the same signal doesn't need to appear
                              twice, and it reads more naturally beside the
                              class chips it's the absence of. */}
                          {s.status !== 'INACTIVE' && s.needsSlot && !showClassColumn && (
                            <span className="whitespace-nowrap rounded-md bg-attention-tint px-2 py-0.5 text-xs font-medium text-attention-foreground">
                              ⚠ {t('list.noSlotChip')}
                            </span>
                          )}
                          {khoaBadge(s.khoaRemaining, t as KhoaBadgeT)}
                        </div>
                      </TableCell>
                      {showClassColumn && (
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <ClassChips classes={s.classes ?? []} t={t} />
                            {s.status !== 'INACTIVE' && s.needsSlot && (
                              <span className="whitespace-nowrap rounded-md bg-attention-tint px-2 py-0.5 text-xs font-medium text-attention-foreground">
                                ⚠ {t('list.noSlotChip')}
                              </span>
                            )}
                          </div>
                        </TableCell>
                      )}
                      <TableCell>
                        {s.client && (
                          <Link
                            to={`/clients/${s.client.id}`}
                            className="inline-flex items-center gap-1 text-brand-700 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                            title={t('list.openPayer')}
                          >
                            {s.client.name}
                            <ArrowRight className="h-3.5 w-3.5 opacity-60" strokeWidth={2} />
                          </Link>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <Link to={`/students/${s.id}`} className="block text-surface-600">
                          {new Date(s.createdAt).toLocaleDateString('vi-VN')}
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <CardList>
              {data.items.map((s) => (
                <CardRow
                  key={s.id}
                  to={`/students/${s.id}`}
                  title={
                    <>
                      {s.name}
                      {s.nickname ? (
                        <span className="ml-1.5 font-normal text-surface-500">({s.nickname})</span>
                      ) : null}
                    </>
                  }
                  titleRight={cardBadge(s)}
                  // The payer is PLAIN TEXT here, not the link the table cell
                  // uses: the whole card is already an <a> to the student, and
                  // an anchor inside an anchor is invalid HTML. The payer link
                  // survives one tap away, on the student's own page.
                  line2={[(s.subjects ?? []).map((sub) => sub.name).join(', '), s.client?.name ?? '']
                    .filter(Boolean)
                    .join(' · ')}
                  // THI-267 Task 15: the phone card's class line, shown under the
                  // same condition as the desktop column (`showClassColumn`) —
                  // text, not chips, and undefined (not rendered) rather than an
                  // empty line for a student in no classes.
                  line3={showClassColumn ? classesCardLine(s.classes ?? [], t as ClassesT) : null}
                  right={khoaBadge(s.khoaRemaining, t as KhoaBadgeT)}
                />
              ))}
            </CardList>
          </>
        )}
      </div>

      {data && (
        <PaginationFooter
          page={data.page}
          pageSize={data.pageSize}
          total={data.total}
          onPageChange={setPage}
          summary={t('list.pageOf', {
            page: data.page,
            pages: pageCount(data.total, data.pageSize),
            total: data.total,
          })}
          prevLabel={t('list.prev')}
          nextLabel={t('list.next')}
        />
      )}

      <CreateStudentDialog
        open={open}
        onOpenChange={setOpen}
      />
      <ImportWizard
        open={importOpen}
        onOpenChange={setImportOpen}
      />
    </div>
  );
}

/** The empty form, hoisted so it is one fixed object rather than a fresh
 *  literal per render. */
const INITIAL_IDENTITY: StudentIdentityValues = {
  name: '', nickname: '', dateOfBirth: '', gender: null,
};

function CreateStudentDialog({
  open, onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation('students');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>{t('create.title')}</DialogTitle>
          <DialogDescription>{t('create.description')}</DialogDescription>
        </DialogHeader>
        {/* Mounted only while open, the same way EditStudentDialog is. Cancelling
            or clicking outside used to leave every field intact, so the next
            student inherited the last one's gender, birth date and tags — and a
            Select always displays something, so a leftover "Nữ" reads as a
            deliberate choice rather than stale state. */}
        {open && <CreateStudentForm onOpenChange={onOpenChange} />}
      </DialogContent>
    </Dialog>
  );
}

function CreateStudentForm({ onOpenChange }: { onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation('students');
  const { mutate: create, isPending } = useCreateStudent();
  const { data: clients } = useClientOptions();

  const [values, setValues] = useState<StudentIdentityValues>(INITIAL_IDENTITY);
  const patch = (p: Partial<StudentIdentityValues>) => setValues((v) => ({ ...v, ...p }));
  const [localTags, setLocalTags] = useState<string[]>([]);
  const [selfPay, setSelfPay] = useState(false);
  const [existingClientId, setExistingClientId] = useState('');
  const [payerName, setPayerName] = useState('');
  const [payerPhone, setPayerPhone] = useState('');
  // Dup-payer soft-warn: fires only on blur (not per keystroke).
  const [lookupPhone, setLookupPhone] = useState('');
  const { data: dupClient } = useClientLookup(lookupPhone);
  const showDupWarning =
    !existingClientId && Boolean(dupClient) && lookupPhone.trim() === payerPhone.trim();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    // self-pay → new payer using the student's own name; otherwise existing parent
    const payer = selfPay
      ? { mode: 'new' as const, name: values.name, zaloPhone: payerPhone || null }
      : existingClientId
        ? { mode: 'existing' as const, clientId: existingClientId }
        : { mode: 'new' as const, name: payerName, zaloPhone: payerPhone || null };
    create(
      {
        name: values.name,
        nickname: values.nickname || null,
        dateOfBirth: values.dateOfBirth || null,
        gender: values.gender,
        tags: localTags,
        payer,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  const canSubmit = values.name && (selfPay || existingClientId || payerName);

  return (
    <form onSubmit={submit}>
      <div className="grid gap-4 py-4">
        <StudentIdentityFields values={values} onChange={patch} idPrefix="create" markRequired />

        <div className="grid gap-2">
          <Label>{t('create.tags')}</Label>
          <Tags
            tags={localTags}
            mode="edit"
            onAdd={(tag) => setLocalTags((prev) => [...prev, tag])}
            onRemove={(tag) => setLocalTags((prev) => prev.filter((t2) => t2 !== tag))}
          />
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={selfPay} onChange={(e) => setSelfPay(e.target.checked)} />
          {t('create.selfPay')}
        </label>

        {!selfPay && (
          <div className="grid gap-3 border-t pt-3">
            <Label>
              {t('create.payerSection')}
              <span aria-hidden="true" className="text-red-500 ml-0.5">*</span>
            </Label>
            <div className="grid gap-2">
              <Label className="text-xs text-muted-foreground">{t('create.existingPayer')}</Label>
              <ClientCombobox
                options={clients ?? []}
                value={existingClientId}
                onSelect={setExistingClientId}
                placeholder="—"
                searchPlaceholder={t('create.payerSearchPlaceholder')}
                emptyText={t('create.payerEmpty')}
              />
            </div>
            {!existingClientId && (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="pname" className="text-xs text-muted-foreground">{t('create.newPayer')}</Label>
                  <Input id="pname" value={payerName} onChange={(e) => setPayerName(e.target.value)} placeholder={t('create.payerName')} />
                </div>
                <div className="grid gap-2">
                  <Input
                    value={payerPhone}
                    onChange={(e) => setPayerPhone(e.target.value)}
                    onBlur={(e) => setLookupPhone(e.target.value)}
                    placeholder={t('create.payerPhone')}
                  />
                  {showDupWarning && dupClient && (
                    <p className="text-xs text-amber-600">
                      {t('create.dupPayerWarning', { name: dupClient.name })}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {selfPay && (
          <div className="grid gap-2">
            <Label htmlFor="spphone">{t('create.payerPhone')}</Label>
            <Input
              id="spphone"
              value={payerPhone}
              onChange={(e) => setPayerPhone(e.target.value)}
              onBlur={(e) => setLookupPhone(e.target.value)}
            />
            {showDupWarning && dupClient && (
              <p className="text-xs text-amber-600">
                {t('create.dupPayerWarning', { name: dupClient.name })}
              </p>
            )}
          </div>
        )}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('create.cancel')}</Button>
        <Button type="submit" disabled={isPending || !canSubmit}>
          {isPending ? t('create.submitting') : t('create.submit')}
        </Button>
      </DialogFooter>
    </form>
  );
}
